

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { all_routes } from '../../../feature-module/router/all_routes';
import { Teacher } from '../../data/types/teacher';
import { teacherinputs } from '../inputoption/inputoption';
import { schoolClassId, classSectionId } from '../../../core/common/selectoption/selectoption';
import CommonSelect from '../../../core/common/commonSelect';
import CustomInput from '../../../core/common/commonInput';
import CustomFileInput from '../../../core/common/CustomFileInput';
import { updateTeacherApi, fetchUserDetailApi } from '../../../core/data/services/teacherService';
import {fetchuserDetailSuccess} from"../../data/redux/userSlice"
import {  addteacherFailure, updateteacherStart, updateteacherSuccess, updateteacherFailure } from '../../../core/data/redux/teacherSlice';
import { getMandatoryFields } from '../../../core/common/commonServices';
import { AppDispatch } from '../../../core/data/redux/store';
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { REACT_APP_AWS_REGION, REACT_APP_AWS_ACCESS_KEY_ID, REACT_APP_AWS_SECRET_ACCESS_KEY, REACT_APP_AWS_S3_BUCKET_NAME } from '../../../constants/constants';

type Option = { label: string; value: string };

interface Role {
    id: number;
    name: string;
    isActive: boolean;
    isDeleted: boolean;
    roleKey: string;
    referenceKey: string;
    description: string;
    createdAt: string;
    updatedAt: string;
}

const s3 = new S3Client({
    region: REACT_APP_AWS_REGION || 'us-east-1',
    credentials: {
        accessKeyId: REACT_APP_AWS_ACCESS_KEY_ID || 'YOUR_AWS_ACCESS_KEY_ID',
        secretAccessKey: REACT_APP_AWS_SECRET_ACCESS_KEY || 'YOUR_AWS_SECRET_ACCESS_KEY',
    },
});

async function uploadFileToS3(file: File): Promise<string> {
    const maxSize = 4 * 1024 * 1024;
    if (file.size > maxSize) {
        throw new Error('File size exceeds 4MB limit');
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
        throw new Error('Invalid file type. Only JPG, PNG, and SVG are allowed');
    }

    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const fileName = `Uploads/${ timestamp }_${ randomString }.${ fileExtension }`;

    try {
        const arrayBuffer = await file.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);

        const command = new PutObjectCommand({
            Bucket: REACT_APP_AWS_S3_BUCKET_NAME,
            Key: fileName,
            Body: uint8Array,
            ContentType: file.type,
        });

        await s3.send(command);
        return `https://${ REACT_APP_AWS_S3_BUCKET_NAME }.s3.${ REACT_APP_AWS_REGION }.amazonaws.com/${ fileName }`;
    } catch (error) {
        console.error('S3 upload error:', error);
        throw new Error('Upload failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
}

const validateTeacherForm = (formData: Omit<Teacher, 'id'>, mandatoryFields: string[]) => {
    const errors: { [key: string]: string } = {};

    mandatoryFields.forEach((field) => {
        if (!formData[field as keyof typeof formData] || String(formData[field as keyof typeof formData]).trim() === '') {
            errors[field] = `${ field } is required`;
        }
    });

    if (formData.firstName && formData.firstName.trim().length < 3) {
        errors.firstName = 'First name must be at least 3 characters long';
    }

    if (formData.phone) {
        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(formData.phone)) {
            errors.phone = 'Phone number must be exactly 10 digits';
        }
    }

    if (formData.aadharNumber) {
        const aadharRegex = /^\d{12}$/;
        if (!aadharRegex.test(formData.aadharNumber)) {
            errors.aadharNumber = 'Aadhar number must be exactly 12 digits';
        }
    }

    if (formData.email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }
    }

    return errors;
};


const getInitialFormState = (): Omit<Teacher, 'id'> => ({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    profileImage: '',
    aadharNumber: '',
    designation: '',
    schoolClassId: '',
    classSectionId: '',
    roleIds: [8],
    parameters: [2],
    role: 'No role',
});

const EditUserProfile: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { state } = useLocation();
    const teacherFromState = state?.teacher as Teacher | undefined;
    console.log("user profile ", teacherFromState)
    const isEdit = Boolean(teacherFromState);

    const [formData, setFormData] = useState<Omit<Teacher, 'id'>>(getInitialFormState());
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [roles] = useState<Role[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    

    const mandatoryFields = getMandatoryFields(teacherinputs);

    const optionsMap: Record<string, Option[]> = {
        designation: roles.map((role) => ({
            label: role.name,
            value: role.name,
        })),
        schoolClassId,
        classSectionId,
    };



    useEffect(() => {
        if (isEdit && teacherFromState) {
            const { id, ...rest } = teacherFromState;
            setFormData({
                ...getInitialFormState(),
                ...rest,
                roleIds: teacherFromState.roleIds || [8],
                parameters: teacherFromState.parameters || [2],
                role: teacherFromState.role || teacherFromState.designation || 'No role',
            });
        }
    }, [isEdit, teacherFromState]);

    const handleInputChange = (key: keyof Omit<Teacher, 'id'>, value: any) => {
        setFormData((prev) => ({
            ...prev,
            [key]: value,
        }));
        setErrors((prev) => {
            if (prev[key as string]) {
                const updated = { ...prev };
                delete updated[key as string];
                return updated;
            }
            return prev;
        });
    };

    const handleSelectChange = (key: keyof Omit<Teacher, 'id'>) => (selected: any) => {
        const selectedValue = selected?.value || '';
        if (key === 'designation') {
            const selectedRole = roles.find((role) => role.name === selectedValue);
            if (selectedRole) {
                setFormData((prev) => ({
                    ...prev,
                    designation: selectedValue,
                    roleIds: [selectedRole.id],
                    role: selectedValue || 'No role',
                }));
            } else {
                handleInputChange(key, selectedValue);
            }
        } else {
            handleInputChange(key, selectedValue);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        

        const newErrors = validateTeacherForm(formData, mandatoryFields);
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            return;
        }

        setLoading(true);
        try {
            if (isEdit && teacherFromState) {
                const changedFields: Partial<Teacher> & { id: number } = {
                    id: teacherFromState.id,
                };
                let anyChange = false;
                Object.keys(formData).forEach((key) => {
                    const k = key as keyof Omit<Teacher, 'id'>;
                    if (formData[k] !== (teacherFromState as any)[k]) {
                        (changedFields as any)[k] = formData[k];
                        anyChange = true;
                    }
                });
                if (!anyChange) {
                   
                    navigate(all_routes.userViewProfile);
                } else {
                    dispatch(updateteacherStart());
                    const updatedResp = await updateTeacherApi(changedFields as Teacher);
                    const updatedTeacher: Teacher = {
                        ...formData,
                        id: teacherFromState.id,
                        role: formData.designation || 'No role',
                        ...(updatedResp as any).data || updatedResp,
                    };
                    console.log('Updated teacher:', updatedTeacher);
                    dispatch(updateteacherSuccess(updatedTeacher));
                    
                    const userDetail = await fetchUserDetailApi({ userId: teacherFromState.id });
                    dispatch(fetchuserDetailSuccess(userDetail));
                    navigate(all_routes.userViewProfile);
                }
            }
            setErrors({});
        } catch (err: any) {
            console.error('Error submitting form:', err);
            let errorMessage = 'Something went wrong. Please try again.';
            if (err?.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.name === 'ApiError' && err.message) {
                errorMessage = err.message;
            } else if (err?.message && err.message !== 'Request failed with status code 400') {
                errorMessage = err.message;
            }
        
            dispatch(isEdit ? updateteacherFailure(errorMessage) : addteacherFailure(errorMessage));
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className="page-wrapper">
            <div className="content content-two">
                <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
                    <div className="my-auto mb-2">
                        <h3 className="mb-1">Edit Profile</h3>
                        <nav>
                            <ol className="breadcrumb mb-0">
                                {/* <li className="breadcrumb-item">
                                    <Link to={all_routes.adminDashboard}>Dashboard</Link>
                                </li> */}
                                <li className="breadcrumb-item">
                                    <Link to={all_routes.userViewProfile}>User Profile</Link>
                                </li>
                                <li className="breadcrumb-item active" aria-current="page">Edit</li>
                            </ol>
                        </nav>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-12">
                        <form onSubmit={handleSubmit}>
                            <div className="card mb-4">
                                <div className="card-header bg-light">
                                    <h4 className="text-dark">Edit Profile</h4>
                                </div>
                                <div className="card-body pb-1">
                                    <div className="row row-cols-xxl-5 row-cols-md-6">
                                        {teacherinputs.map(({ label, type, key, mandatory }: any) => (
                                            <div className="col-xxl col-xl-3 col-md-6" key={key}>
                                                <div className="mb-3">
                                                    <label className="form-label">
                                                        {label}
                                                        {mandatory && <span style={{ color: 'red' }}> *</span>}
                                                    </label>
                                                    {key === 'faceImage' ? (
                                                        <div className="profile-upload">
                                                            <div className="profile-uploader d-flex align-items-center">
                                                                <div className="drag-upload-btn mb-3">
                                                                    {formData.faceImage && (
                                                                        <img
                                                                            src={String(formData.faceImage)}
                                                                            alt="Profile Preview"
                                                                            style={{ width: 60, height: 60, objectFit: 'cover', marginTop: 8 }}
                                                                        />
                                                                    )}
                                                                    Upload
                                                                    <CustomFileInput
                                                                        id={key}
                                                                        className="form-control image-sign"
                                                                        accept=".jpg,.jpeg,.png,.svg"
                                                                        onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                            const file = e.target.files?.[0];
                                                                            if (file) {
                                                                                try {
                                                                                    const s3Url = await uploadFileToS3(file);
                                                                                    handleInputChange(key as keyof Omit<Teacher, 'id'>, s3Url);
                                                                                } catch (err) {
                                                                                    console.error('Image upload failed:', err);
                                                                                    alert('Image upload failed. Please try again.');
                                                                                }
                                                                            } else {
                                                                                handleInputChange(key as keyof Omit<Teacher, 'id'>, null);
                                                                            }
                                                                        }}
                                                                    />
                                                                </div>
                                                                <Link
                                                                    to="#"
                                                                    className="btn btn-primary mb-3"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        handleInputChange(key as keyof Omit<Teacher, 'id'>, null);
                                                                    }}
                                                                >
                                                                    Remove
                                                                </Link>
                                                            </div>
                                                            <p className="fs-12">Upload image size 4MB, Format JPG, PNG, SVG</p>
                                                        </div>
                                                    ) : type === 'select' ? (
                                                        <CommonSelect
                                                            className="select"
                                                            options={optionsMap[key] || []}
                                                            defaultValue={
                                                                isEdit && formData[key] !== null && formData[key] !== ''
                                                                    ? { value: String(formData[key]), label: String(formData[key]) }
                                                                    : undefined
                                                            }
                                                            onChange={handleSelectChange(key as keyof Omit<Teacher, 'id'>)}
                                                        />
                                                    ) : (
                                                        <CustomInput
                                                            id={key}
                                                            className="form-control"
                                                            placeholder={label}
                                                            value={String(formData[key as keyof typeof formData] || '')}
                                                            onChange={(e) => handleInputChange(key as keyof Omit<Teacher, 'id'>, e.target.value)}
                                                        />
                                                    )}
                                                    {errors[key] && <div className="text-danger">{errors[key]}</div>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="text-end">
                                <button
                                    type="button"
                                    className="btn btn-light me-3"
                                    onClick={() => navigate(all_routes.userViewProfile)}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={loading}>
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditUserProfile;